// Initial Setup Page
import { APP_VERSION } from '../lib/version';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Loader2, Check, ChevronRight, ChevronLeft, Upload } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { setupApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const setupSchema = z.object({
  setup_key: z.string().min(1, 'Setup-Schlüssel erforderlich'),
  wache_name: z.string().min(1, 'Wachname erforderlich'),
  wache_nummer: z.string().min(1, 'Wachnummer erforderlich'),
  adresse: z.string().optional(),
  admin_vorname: z.string().min(1, 'Vorname erforderlich'),
  admin_nachname: z.string().min(1, 'Nachname erforderlich'),
  admin_loginname: z.string().min(3, 'Loginname mind. 3 Zeichen'),
  admin_email: z.string().email('Gültige E-Mail erforderlich').optional().or(z.literal('')),
  admin_passwort: z.string().min(12, 'Passwort mind. 12 Zeichen'),
  anzahl_abteilungen: z.number().min(1).max(10).default(4),
});

export function SetupPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [setupResult, setSetupResult] = useState(null);

  const { register, handleSubmit, formState: { errors }, watch, trigger, setValue } = useForm({
    resolver: zodResolver(setupSchema),
    defaultValues: {
      setup_key: '',
      wache_name: '',
      wache_nummer: '',
      adresse: '',
      admin_vorname: '',
      admin_nachname: '',
      admin_loginname: '',
      admin_email: '',
      admin_passwort: '',
      anzahl_abteilungen: 4,
    },
  });

  // Check if setup is needed
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await setupApi.getStatus();
        if (!response.data.needs_setup) {
          navigate('/login');
        }
      } catch (error) {
        console.error('Setup status check failed:', error);
      }
    };
    checkSetup();
  }, [navigate]);

  const nextStep = async () => {
    let fieldsToValidate = [];
    if (step === 1) {
      fieldsToValidate = ['wache_name', 'wache_nummer'];
    } else if (step === 2) {
      fieldsToValidate = ['admin_vorname', 'admin_nachname', 'admin_loginname'];
    }
    
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const response = await setupApi.initialSetup(data);
      setSetupResult(response.data);
      setStep(4); // Success step
      toast.success('Wache erfolgreich eingerichtet!');
    } catch (error) {
      console.error('Setup error:', error);
      const detail = error.response?.data?.detail;
      let errorMsg = 'Setup fehlgeschlagen';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-6 animate-fade-in">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-20 h-20">
            <img src="/logo-fwonline.svg" alt="Feuerwache Logo" className="w-full h-full" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Digitale Feuerwache
          </h1>
          <p className="text-muted-foreground">Initial-Setup</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step > s ? 'bg-primary text-primary-foreground' :
                  step === s ? 'bg-primary text-primary-foreground' :
                  'bg-muted text-muted-foreground'
                }`}
              >
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 rounded ${step > s ? 'bg-primary' : 'bg-muted'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Setup Card */}
        <Card className="shadow-lg">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Step 1: Wache Info */}
            {step === 1 && (
              <>
                <CardHeader>
                  <CardTitle>Wache einrichten</CardTitle>
                  <CardDescription>
                    Geben Sie die Grunddaten Ihrer Feuerwache ein
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="wache_name">Wachname *</Label>
                    <Input
                      id="wache_name"
                      placeholder="z.B. Feuerwache Mitte"
                      data-testid="setup-wache-name"
                      {...register('wache_name')}
                    />
                    {errors.wache_name && (
                      <p className="text-sm text-destructive">{errors.wache_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wache_nummer">Wachnummer *</Label>
                    <Input
                      id="wache_nummer"
                      placeholder="z.B. 4600"
                      data-testid="setup-wache-nummer"
                      {...register('wache_nummer')}
                    />
                    {errors.wache_nummer && (
                      <p className="text-sm text-destructive">{errors.wache_nummer.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adresse">Adresse</Label>
                    <Input
                      id="adresse"
                      placeholder="Straße, PLZ Ort"
                      data-testid="setup-adresse"
                      {...register('adresse')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="anzahl_abteilungen">Anzahl Wachabteilungen</Label>
                    <Input
                      id="anzahl_abteilungen"
                      type="number"
                      min={1}
                      max={10}
                      data-testid="setup-abteilungen"
                      {...register('anzahl_abteilungen', { valueAsNumber: true })}
                    />
                  </div>

                  <Button type="button" onClick={nextStep} className="w-full" data-testid="setup-next-1">
                    Weiter <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </>
            )}

            {/* Step 2: First Developer */}
            {step === 2 && (
              <>
                <CardHeader>
                  <CardTitle>Developer anlegen</CardTitle>
                  <CardDescription>
                    Erstellen Sie den ersten, vollständig berechtigten Developer-Account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="admin_vorname">Vorname *</Label>
                      <Input
                        id="admin_vorname"
                        placeholder="Vorname"
                        data-testid="setup-admin-vorname"
                        {...register('admin_vorname')}
                      />
                      {errors.admin_vorname && (
                        <p className="text-sm text-destructive">{errors.admin_vorname.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admin_nachname">Nachname *</Label>
                      <Input
                        id="admin_nachname"
                        placeholder="Nachname"
                        data-testid="setup-admin-nachname"
                        {...register('admin_nachname')}
                      />
                      {errors.admin_nachname && (
                        <p className="text-sm text-destructive">{errors.admin_nachname.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_loginname">Loginname *</Label>
                    <Input
                      id="admin_loginname"
                      placeholder="z.B. mustermann"
                      data-testid="setup-admin-loginname"
                      {...register('admin_loginname')}
                    />
                    {errors.admin_loginname && (
                      <p className="text-sm text-destructive">{errors.admin_loginname.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_email">E-Mail (optional)</Label>
                    <Input
                      id="admin_email"
                      type="email"
                      placeholder="email@beispiel.de"
                      data-testid="setup-admin-email"
                      {...register('admin_email')}
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={prevStep} className="flex-1">
                      <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                    </Button>
                    <Button type="button" onClick={nextStep} className="flex-1" data-testid="setup-next-2">
                      Weiter <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <>
                <CardHeader>
                  <CardTitle>Bestätigung</CardTitle>
                  <CardDescription>
                    Überprüfen Sie Ihre Eingaben
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg bg-muted p-4 space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Wache</p>
                      <p className="font-medium">{watch('wache_name')} (FW {watch('wache_nummer')})</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Abteilungen</p>
                      <p className="font-medium">{watch('anzahl_abteilungen')} Wachabteilungen</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Developer</p>
                      <p className="font-medium">
                        {watch('admin_vorname')} {watch('admin_nachname')} ({watch('admin_loginname')})
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="admin_passwort">Developer-Passwort *</Label>
                    <Input
                      id="admin_passwort"
                      type="password"
                      placeholder="Mindestens 12 Zeichen"
                      data-testid="setup-admin-passwort"
                      {...register('admin_passwort')}
                    />
                    {errors.admin_passwort && (
                      <p className="text-sm text-destructive">{errors.admin_passwort.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="setup_key">Einmaliger Setup-Schlüssel *</Label>
                    <Input
                      id="setup_key"
                      type="password"
                      placeholder="Aus der Server-Konfiguration"
                      data-testid="setup-key"
                      {...register('setup_key')}
                    />
                    {errors.setup_key && (
                      <p className="text-sm text-destructive">{errors.setup_key.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Dieser Schlüssel wird nur für die erstmalige Einrichtung benötigt.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={prevStep} className="flex-1">
                      <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                    </Button>
                    <Button type="submit" className="flex-1" disabled={isLoading} data-testid="setup-submit">
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Einrichten...
                        </>
                      ) : (
                        <>
                          Wache einrichten <Check className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </>
            )}

            {/* Step 4: Success */}
            {step === 4 && setupResult && (
              <>
                <CardHeader>
                  <CardTitle className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
                      <Check className="w-8 h-8" />
                    </div>
                    <br />
                    Setup erfolgreich!
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg bg-muted p-4 space-y-3">
                    <p className="text-sm text-muted-foreground">Ihre Zugangsdaten:</p>
                    <div>
                      <p className="text-sm text-muted-foreground">Loginname</p>
                      <p className="font-mono font-medium">{watch('admin_loginname')}</p>
                    </div>
                    {setupResult.admin_temp_passwort && (
                      <div>
                        <p className="text-sm text-muted-foreground">Temporäres Passwort</p>
                        <p className="font-mono font-medium bg-yellow-100 dark:bg-yellow-900 px-2 py-1 rounded">
                          {setupResult.admin_temp_passwort}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Bitte notieren Sie sich dieses Passwort!
                        </p>
                      </div>
                    )}
                  </div>

                  <Button onClick={goToLogin} className="w-full" data-testid="setup-go-login">
                    Zur Anmeldung
                  </Button>
                </CardContent>
              </>
            )}
          </form>
        </Card>
        
        {/* Footer with Version */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          © {new Date().getFullYear()} Creativey Media · Version {APP_VERSION}
        </p>
      </div>
    </div>
  );
}

export default SetupPage;
